import { useState, useEffect, useRef } from 'react';
import { Plus, Upload, Trash2, Users, ChevronLeft, ChevronRight, ClipboardPaste, X, Check } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

export default function Leads() {
  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [showNewList, setShowNewList] = useState(false);
  const [showPaste, setShowPaste] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasting, setPasting] = useState(false);
  const fileRef = useRef();
  const limit = 50;

  useEffect(() => { loadLists(); }, []);
  useEffect(() => { if (selectedList) loadLeads(selectedList.id, 1); }, [selectedList]);

  const loadLists = async () => {
    const res = await api.get('/api/leads/lists');
    setLists(res.data.lists || []);
  };

  const loadLeads = async (listId, p = page) => {
    setLoading(true);
    try {
      const res = await api.get(`/api/leads/lists/${listId}/leads?page=${p}&limit=${limit}`);
      setLeads(res.data.leads || []);
      setTotal(res.data.total || 0);
      setPage(p);
    } finally { setLoading(false); }
  };

  const createList = async () => {
    if (!newListName.trim()) return;
    const res = await api.post('/api/leads/lists', { name: newListName });
    setLists(l => [res.data.list, ...l]);
    setNewListName('');
    setShowNewList(false);
    setSelectedList(res.data.list);
    toast.success('List created!');
  };

  const deleteList = async (id, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!window.confirm('Delete this entire list and all its leads? This cannot be undone.')) return;
    await api.delete(`/api/leads/lists/${id}`);
    setLists(l => l.filter(x => x.id !== id));
    if (selectedList?.id === id) { setSelectedList(null); setLeads([]); setTotal(0); }
    toast.success('List deleted');
  };

  const deleteLead = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    await api.delete(`/api/leads/${id}`);
    setLeads(l => l.filter(x => x.id !== id));
    setTotal(t => t - 1);
    toast.success('Lead deleted');
  };

  const importFile = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedList) return;
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await api.post(`/api/leads/lists/${selectedList.id}/import`, form);
      toast.success(`Imported ${res.data.imported} leads!`);
      loadLeads(selectedList.id, 1);
      loadLists();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Import failed');
    }
    e.target.value = '';
  };

  const pasteLeads = async () => {
    if (!pasteText.trim()) return toast.error('Paste some emails first');
    setPasting(true);
    try {
      const res = await api.post(`/api/leads/lists/${selectedList.id}/paste`, { text: pasteText });
      toast.success(`Imported ${res.data.imported} leads from paste!`);
      setPasteText('');
      setShowPaste(false);
      loadLeads(selectedList.id, 1);
      loadLists();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Paste failed');
    } finally { setPasting(false); }
  };

  const pages = Math.ceil(total / limit);

  return (
    <div style={{ display: 'flex', gap: 20, height: 'calc(100vh - 56px)' }}>

      {/* Left sidebar — lists */}
      <div style={{ width: 240, minWidth: 240, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <h1 style={{ fontSize: 16, fontWeight: 600 }}>Lead lists</h1>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowNewList(!showNewList)}><Plus size={14} /></button>
        </div>

        {showNewList && (
          <div style={{ display: 'flex', gap: 6 }}>
            <input className="input" placeholder="List name…" value={newListName} onChange={e => setNewListName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && createList()} style={{ flex: 1, fontSize: 12 }} autoFocus />
            <button className="btn btn-primary btn-sm" onClick={createList}><Check size={12} /></button>
          </div>
        )}

        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {lists.length === 0 ? (
            <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>No lists yet</div>
          ) : lists.map(list => (
            <div key={list.id} onClick={() => setSelectedList(list)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', borderRadius: 'var(--radius-sm)', cursor: 'pointer', background: selectedList?.id === list.id ? 'var(--bg3)' : 'transparent', border: `1px solid ${selectedList?.id === list.id ? 'var(--border2)' : 'transparent'}` }}>
              <Users size={13} color={selectedList?.id === list.id ? 'var(--blue)' : 'var(--text3)'} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{list.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>{parseInt(list.lead_count) || 0} leads</div>
              </div>
              <button onClick={e => deleteList(list.id, e)} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', padding: 2, flexShrink: 0 }}>
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Main area */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {!selectedList ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center' }}>
              <Users size={40} color="var(--text3)" style={{ marginBottom: 14 }} />
              <p style={{ color: 'var(--text2)', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>Select a list to view leads</p>
              <p style={{ color: 'var(--text3)', fontSize: 12 }}>Or create a new list to get started</p>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div>
                <h2 style={{ fontSize: 16, fontWeight: 600 }}>{selectedList.name}</h2>
                <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{total.toLocaleString()} leads</p>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setShowPaste(!showPaste)}>
                  <ClipboardPaste size={13} /> Paste emails
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => fileRef.current.click()}>
                  <Upload size={13} /> Import CSV/TXT
                </button>
                <button className="btn btn-danger btn-sm" onClick={() => deleteList(selectedList.id, { stopPropagation: () => {} })}>
                  <Trash2 size={13} /> Delete list
                </button>
                <input ref={fileRef} type="file" accept=".csv,.txt" style={{ display: 'none' }} onChange={importFile} />
              </div>
            </div>

            {/* Paste box */}
            {showPaste && (
              <div className="card" style={{ padding: 16, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>Paste emails</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>Paste any text containing emails — one per line, comma separated, or mixed. Bulkify will extract all valid emails automatically.</div>
                  </div>
                  <button onClick={() => { setShowPaste(false); setPasteText(''); }} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer' }}><X size={14} /></button>
                </div>
                <textarea className="input" rows={6} placeholder={`john@example.com\njane@store.com, mike@business.com\nOr paste any text with emails mixed in...`}
                  value={pasteText} onChange={e => setPasteText(e.target.value)}
                  style={{ resize: 'vertical', lineHeight: 1.6, marginBottom: 10 }} />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                    {pasteText ? `${(pasteText.match(/[\w.+-]+@[\w.-]+\.[a-zA-Z]{2,}/g) || []).length} emails detected` : ''}
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={pasteLeads} disabled={pasting}>
                    <ClipboardPaste size={12} /> {pasting ? 'Importing…' : 'Import emails'}
                  </button>
                </div>
              </div>
            )}

            {/* Leads table */}
            <div className="card" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {loading ? (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>Loading…</div>
              ) : leads.length === 0 ? (
                <div style={{ padding: 48, textAlign: 'center' }}>
                  <Users size={32} color="var(--text3)" style={{ marginBottom: 12 }} />
                  <p style={{ color: 'var(--text2)', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>No leads yet</p>
                  <p style={{ color: 'var(--text3)', fontSize: 12 }}>Import a CSV, TXT file or paste emails above</p>
                </div>
              ) : (
                <>
                  <div style={{ overflowY: 'auto', flex: 1 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead style={{ position: 'sticky', top: 0, background: 'var(--bg2)', zIndex: 1 }}>
                        <tr style={{ borderBottom: '1px solid var(--border)' }}>
                          {['Email', 'First name', 'Last name', 'Company', 'Status', ''].map(h => (
                            <th key={h} style={{ padding: '9px 16px', textAlign: 'left', fontSize: 11, color: 'var(--text3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {leads.map((lead, i) => (
                          <tr key={lead.id} style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={{ padding: '10px 16px', fontSize: 13 }}>{lead.email}</td>
                            <td style={{ padding: '10px 16px', fontSize: 13, color: 'var(--text2)' }}>{lead.first_name || '—'}</td>
                            <td style={{ padding: '10px 16px', fontSize: 13, color: 'var(--text2)' }}>{lead.last_name || '—'}</td>
                            <td style={{ padding: '10px 16px', fontSize: 13, color: 'var(--text2)' }}>{lead.company || '—'}</td>
                            <td style={{ padding: '10px 16px' }}>
                              <span className={`badge ${lead.status === 'active' ? 'badge-blue' : lead.status === 'bounced' ? 'badge-red' : lead.status === 'unsubscribed' ? 'badge-amber' : 'badge-gray'}`}>
                                {lead.status}
                              </span>
                            </td>
                            <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                              <button onClick={() => deleteLead(lead.id)} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', padding: 4 }}>
                                <Trash2 size={13} color="var(--red)" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {pages > 1 && (
                    <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, color: 'var(--text2)' }}>
                      <span>Showing {((page-1)*limit)+1}–{Math.min(page*limit, total)} of {total.toLocaleString()}</span>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => loadLeads(selectedList.id, page-1)} disabled={page === 1}>
                          <ChevronLeft size={13} />
                        </button>
                        <span style={{ padding: '4px 8px', fontSize: 12 }}>Page {page} of {pages}</span>
                        <button className="btn btn-ghost btn-sm" onClick={() => loadLeads(selectedList.id, page+1)} disabled={page === pages}>
                          <ChevronRight size={13} />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
